import React, { useMemo, useEffect, useState } from 'react';
import SectionCard from '../components/SectionCard.jsx';
import NestedSection from '../components/NestedSection.jsx';
import KPICard from '../components/KPICard.jsx';
import DataTable from '../components/DataTable.jsx';
import MoneyCell from '../components/MoneyCell.jsx';
import EduTip from '../components/EduTip.jsx';
import { shapeIncomeStatement, shapeBalanceSheet, shapeCashFlowStatement, shapeIncomeStatementMulti, shapeBalanceSheetMulti, shapeCashFlowStatementMulti } from '../components/FormalStatementTable.jsx';
import FormalStatementSection from '../components/FormalStatementSection.jsx';
import { makeShowBlock, SubViewBreadcrumb } from '../components/SubViewShell.jsx';
import ModuleSettingsPanel from '../components/ModuleSettingsPanel.jsx';
import ComparativeTablesView from '../components/ComparativeTablesView.jsx';
import ContextPanel from '../components/ContextPanel.jsx';
import ModifiedBadge, {
  isRecurringOverridden, isFamilyDirectOverridden, isMarianOverridden,
  OVERRIDE_REASON,
} from '../components/ModifiedBadge.jsx';
import MiniSparkline, { SparklineDeltaBadge } from '../components/MiniSparkline.jsx';
import { resolveTrendVisual } from '../components/formatAmount.js';
import MetricHorizonTable from '../components/MetricHorizonTable.jsx';
import Tooltip, { RichExplanation } from '../components/Tooltip.jsx';
import { RECURRING, FAMILY_AP, AR_RECURRING } from '../data.js';
import {
  fmtMoney, fmtPct,
  calcMonthlyRecurringSubs, calcYearlySubsAmortized, calcQuarterlySubsAmortized,
  calcMonthlyAllRecurring, calcMonthlyFamilyDirect, calcMonthlyMarian,
  calcScheduledEducationAR, calcExpectedEducationAR, calcMonthlyFamilyPoolAR,
  calcDSO, calcDPO, calcCashConversionCycle, netEducationBurden,
  currentYM, marianArrears,
  recurringByCategory, recurringBreakdown, recurringCategoryBreakdown,
  calcMonthlyMortgages,
  resolveAccountVisibility,
  fxHintFor,
  applyAdjustingEntries,
  buildIncomeStatement, buildCashFlowSnapshot,
  totalAssetsLiquid, totalCreditCardDebt, totalCCCredits,
  totalLongTermLiabilities, totalEquity, totalSuspense,
  totalFixedAssetsNet, totalCCDebtFromBlocks, totalCCCreditsFromBlocks, totalLTLFromBlocks,
  buildThreeStatementForDirection, getTabDirection,
  aggregateByDensity, fmtPeriod, normalizePeriodScalar,
  currentPeriodStateForRows,
  buildFinancialRatiosSeries, buildFinancialRatiosTimeline,
  verbosityAtLeast, getModuleFeatures,
} from '../engine.js';
import { RecurringByCategoryChart, FamilyCompositionChart, ARAPStackedChart } from '../charts/index.jsx';

const TAB_CONTENT = 'flex flex-col [&>*+*]:mt-4';
const KPI_ROW = 'grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mb-5';
const KPI_ROW_COMPACT = 'grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mt-3 mb-0';
const GRID_2 = 'grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4';
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const HINT_WARN = `${HINT} !border-l-accent-gold !text-text`;
const COMPACT_LIST = 'mt-[0.6rem] mb-0 pl-[1.1rem] text-text-muted text-[0.84rem] leading-[1.65]';
const DRIVER_ROW = 'grid grid-cols-[1fr_auto] gap-[0.6rem] items-center py-[0.35rem] border-b border-dashed border-border last:border-b-0 text-[0.85rem]';
const BADGE_FORECAST = 'inline-block text-[0.6rem] font-bold px-[6px] py-px rounded-[3px] uppercase tracking-[0.06em] ml-[0.4rem] align-middle border bg-[rgba(96,165,250,0.18)] text-accent-blue border-[rgba(96,165,250,0.35)]';
const HINT_INLINE = 'inline-block px-1 text-[0.85em] text-text-muted opacity-70 not-italic';

export default function BudgetsTab({ scenario, setScenario, onJump, activeSection, onBackToOverview }) {
  const ym = currentYM();
  /* v8 Path G — focus-mode gate. `show('foo','bar')` returns true when
     activeSection is overview/null OR matches any of the listed names. */
  const show = makeShowBlock(activeSection);
  /* v8 Path J — time-aware ratios. Each ratio now carries a 5-point
     series (−3mo, −1mo, current, +1mo, +3mo) so the grid can render a
     tiny sparkline beside each value. Same ratio definitions applied at
     each timestamp via the snapshot-override pattern in engine. */
  const financialRatios = useMemo(() => buildFinancialRatiosSeries(scenario), [scenario]);

  /* ScenarioBar scalars hoisted to the top of the component so ratio
     timelines + projection useMemos below can reference them without a
     TDZ crash. (Previously declared mid-body, which blanked the page
     when any Budgets sub-view rendered — this was the darkish-blue bug.) */
  const budgetsPeriods = normalizePeriodScalar(scenario.formalPeriodCount, 12);
  const budgetsDensity = normalizePeriodScalar(scenario.formalPeriodDensity, 1);
  const budgetsHorizon = budgetsPeriods * budgetsDensity;
  const budgetsDirection = getTabDirection(scenario, 'budgets');
  const budgetsDirLabel = budgetsDirection === 'past' ? 'trailing' : budgetsDirection === 'future' ? 'leading' : 'spanning';

  /* v8 Path L — full horizon timeline. Uses global Period × Density ×
     Direction from ScenarioBar. Rendered when user toggles Ratios view
     to "Timeline". Same 15 ratios, all N periods, like the 3-statement
     model rolls. */
  const ratiosTimeline = useMemo(() => buildFinancialRatiosTimeline(scenario, {
    direction: budgetsDirection,
    periods: budgetsPeriods,
    density: budgetsDensity,
  }), [scenario, budgetsDirection, budgetsPeriods, budgetsDensity]);

  const [ratiosView, setRatiosView] = useState('snapshot');

  /* v8 Path E — scroll to the requested section when sidebar routes to
     e.g. 'budgets:family-ap'. Section cards wire `id={`section-${sectionId}`}`
     via NestedSection. */
  useEffect(() => {
    if (!activeSection || typeof document === 'undefined') return;
    const t = setTimeout(() => {
      const el = document.getElementById(`section-${activeSection}`) || document.getElementById(activeSection);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.remove('section-focused');
      void el.offsetWidth;
      el.classList.add('section-focused');
      setTimeout(() => el.classList.remove('section-focused'), 1900);
    }, 60);
    return () => clearTimeout(t);
  }, [activeSection]);

  const adj = scenario.includeAdjustments !== false
    ? applyAdjustingEntries(scenario)
    : { byAccount: {}, totals: { is: 0, bs: 0 }, count: 0, entries: [] };
  const isExpenseAdj = adj.count > 0 ? -adj.totals.is : 0;

  const jeExpenseBreakdown = adj.entries
    .filter(e => e.status === 'posted' && (e.affects ?? []).includes('is'))
    .map(e => ({
      label: `JE: ${e.memo || '(no memo)'}`,
      amount: e.lines.reduce((s, l) => s + ((Number(l.debit) || 0) - (Number(l.credit) || 0)), 0),
      source: 'Adjusting Entry',
      cat: 'JE',
      jeId: e.id,
      jeDate: e.date,
    }));

  const is = buildIncomeStatement(scenario);
  const assetsLiquid = totalAssetsLiquid();
  const ccDebt = totalCreditCardDebt();
  const ccCredits = totalCCCredits();
  const ltl = totalLongTermLiabilities();
  const eq = totalEquity();
  const susTotal = totalSuspense();

  const fixedAssetsNet = totalFixedAssetsNet();
  const ccDebtV72      = totalCCDebtFromBlocks();
  const ccCreditsV72   = totalCCCreditsFromBlocks();
  const ltlV72         = totalLTLFromBlocks();

  const budgetsProjection = useMemo(() => {
    if (budgetsPeriods <= 1) return null;
    const raw = buildThreeStatementForDirection(scenario, budgetsDirection, budgetsHorizon, budgetsDensity);
    const forecast         = aggregateByDensity(raw.forecast,         budgetsDensity, 'last');
    const incomeStatements = aggregateByDensity(raw.incomeStatements, budgetsDensity, 'sum');
    const balanceSheets    = aggregateByDensity(raw.balanceSheets,    budgetsDensity, 'last');
    const cashFlows        = aggregateByDensity(raw.cashFlows,        budgetsDensity, 'sum');
    const periodLabels     = forecast.map(r => fmtPeriod(r.ym ?? r.label, budgetsDensity));
    const currentState     = currentPeriodStateForRows(forecast, budgetsDensity);
    const currentLabel     = currentState.label;
    return { forecast, incomeStatements, balanceSheets, cashFlows, periodLabels, currentLabel, currentState };
  }, [scenario, budgetsPeriods, budgetsDensity, budgetsHorizon, budgetsDirection]);
  const budgetsPeriodLabels = budgetsProjection?.periodLabels;
  const budgetsCurrentLabel = budgetsProjection?.currentLabel ?? fmtPeriod(currentYM(), budgetsDensity);
  const budgetsCurrentProof = budgetsProjection?.currentState ?? null;

  const monthlySubs = calcMonthlyRecurringSubs(ym, scenario);
  const yearlyAmort = calcYearlySubsAmortized(ym, scenario);
  const quarterlyAmort = calcQuarterlySubsAmortized(ym, scenario);
  const totalSubs = calcMonthlyAllRecurring(ym, scenario);
  const famDirect = calcMonthlyFamilyDirect(ym, scenario);
  const marian = calcMonthlyMarian(ym, scenario);
  const eduScheduled = calcScheduledEducationAR(scenario);
  const eduExpected = calcExpectedEducationAR(scenario);
  const familyPoolAR = calcMonthlyFamilyPoolAR(ym);
  const netEduBurden = netEducationBurden(scenario, ym);

  const slugBudgets = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const tierFor = (monthly) => monthly >= 1000 ? 'primary' : monthly >= 200 ? 'secondary' : 'dust';

  const allRecurringRows = [
    ...RECURRING.monthly.map((r) => ({
      id: `m-${r.code}-${r.vendor}`,
      accountId: `expense.recurring.monthly.${slugBudgets(r.code + '-' + r.vendor)}`,
      _coaTier: tierFor(r.amount),
      _overridden: isRecurringOverridden(scenario, r),
      vendor: r.vendor, code: r.code, cat: r.cat,
      freq: r.forecastOnly ? 'Monthly · FORECAST' : 'Monthly',
      forecastOnly: !!r.forecastOnly,
      amount: r.amount, monthly: r.amount,
      account: r.account,
      fxHint: fxHintFor(r, scenario, ym),
      zohoRef: r.zohoRef,
      __note: r.downgradeYM ? `Downgrades to ${fmtMoney(r.downgradeAmount)} from ${r.downgradeYM}` : undefined,
    })),
    ...RECURRING.yearly.map((r) => ({
      id: `y-${r.code}-${r.vendor}`,
      accountId: `expense.recurring.yearly.${slugBudgets(r.code + '-' + r.vendor)}`,
      _coaTier: tierFor(r.amount / 12),
      _overridden: isRecurringOverridden(scenario, r),
      vendor: r.vendor, code: r.code, cat: r.cat,
      freq: `Yearly (M${r.month})`,
      amount: r.amount, monthly: r.amount / 12,
      fxHint: fxHintFor(r, scenario, ym),
    })),
    ...RECURRING.quarterly.map((r) => ({
      id: `q-${r.code}-${r.vendor}`,
      accountId: `expense.recurring.quarterly.${slugBudgets(r.code + '-' + r.vendor)}`,
      _coaTier: tierFor(r.amount * 4 / 12),
      _overridden: isRecurringOverridden(scenario, r),
      vendor: r.vendor, code: r.code, cat: r.cat,
      freq: 'Quarterly',
      amount: r.amount, monthly: r.amount * 4 / 12,
      account: r.account,
      fxHint: fxHintFor(r, scenario, ym),
    })),
  ].sort((a, b) => b.monthly - a.monthly);

  /* v8 Path I — scenario-override summary. Counts active overrides per
     category so we can show a section-header summary in addition to the
     per-row dots. Makes the "did I change anything?" answer visible at
     a glance. */
  const overriddenRecurringCount = allRecurringRows.filter(r => r._overridden).length;
  const familyDirectModified = isFamilyDirectOverridden(scenario);
  const marianModified = isMarianOverridden(scenario);

  const recurringRows = allRecurringRows.filter(r =>
    resolveAccountVisibility({ id: r.accountId, tier: r._coaTier }, scenario, 'budgets')
  );
  const hiddenRecurring = allRecurringRows.length - recurringRows.length;
  const top3Recurring = recurringRows.slice(0, 3);

  const famComposition = [
    {
      label: 'Apr 2026',
      cashMari: FAMILY_AP.direct.channels[0].estMonthly,
      groceries: FAMILY_AP.direct.channels[1].estMonthly,
      sundry:    FAMILY_AP.direct.channels[2].estMonthly,
      marian:    marian,
    },
  ];

  const marianPayments = FAMILY_AP.viaMarian.paymentHistory.map((p, i) => ({ id: i, ...p }));
  const top3Marian = [...marianPayments].sort((a, b) => b.amount - a.amount).slice(0, 3);

  const famDirectBreakdown = FAMILY_AP.direct.channels.map(c => ({
    label: c.name, amount: c.estMonthly, source: c.cat, cat: 'Direct support',
  }));

  const siblingRows = AR_RECURRING.educationFund.map((s, i) => {
    let scheduled = s.amount;
    if (s.contactId === 'demo-contact-3') {
      if (scenario.jpMode === 'historical')   scheduled = 5000;
      else if (scenario.jpMode === 'custom')  scheduled = scenario.jpAmount ?? s.amount;
      else                                    scheduled = s.amount;
    }
    else if (s.contactId === 'demo-contact-4')scheduled = scenario.jcAmount ?? s.amount;
    else if (s.contactId === 'demo-contact-5')scheduled = scenario.jmAmount ?? s.amount;
    const expected = scheduled * (s.collectionRate ?? 1);
    return {
      id: i,
      sibling: s.from,
      scheduled,
      rate: s.collectionRate ?? 1,
      expected,
      note: s.note,
    };
  });
  const top3Siblings = [...siblingRows].sort((a, b) => b.expected - a.expected).slice(0, 3);

  const siblingFooter = {
    sibling: 'Total (scheduled vs expected)',
    scheduled: siblingRows.reduce((s, r) => s + r.scheduled, 0),
    rate: null,
    expected: siblingRows.reduce((s, r) => s + r.expected, 0),
    note: '',
  };

  const moneyFmt = (v) => <MoneyCell value={v} decimals={2} />;

  const famChannels = FAMILY_AP.direct.channels.map(c => ({
    label: c.name, amount: c.estMonthly, cat: c.cat,
  }));
  const famAllRows = [
    ...famChannels,
    { label: 'Routed support channel', amount: marian, cat: 'routed' },
  ].sort((a, b) => b.amount - a.amount);
  const famTop3 = famAllRows.slice(0, 3);

  const fvfMRRNow = (scenario.fvfLaunchYM && ym >= scenario.fvfLaunchYM)
    ? (scenario.fvfTargetMRR ?? 0) : 0;
  const arApRows = [
    { counterparty: 'Direct support', ap: famDirect, ar: 0 },
    { counterparty: 'Routed support', ap: marian,    ar: 0 },
    { counterparty: 'Mortgage banks', ap: calcMonthlyMortgages(), ar: 0 },
    { counterparty: 'Recurring vendors', ap: totalSubs, ar: 0 },
    ...siblingRows.map((r) => ({
      counterparty: r.sibling.split('(')[0].trim(),
      ap: 0, ar: r.expected,
    })),
    { counterparty: 'Support pool',   ap: 0, ar: familyPoolAR },
    { counterparty: 'Subscription tenants', ap: 0, ar: fvfMRRNow },
  ]
    .filter(r => (r.ap + r.ar) > 0)
    .sort((a, b) => (b.ap + b.ar) - (a.ap + a.ar));

  /* v8 Path P — per-module feature gates. Each major Budgets block can be
     hidden via the ⚙ panel at the top of overview. Defaults all true. */
  const features = getModuleFeatures(scenario, 'budgets');

  return (
    <div className={TAB_CONTENT}>
      <SubViewBreadcrumb moduleId="budgets" activeSection={activeSection} onBack={onBackToOverview} />
      {activeSection === 'comparative' && (
        <ComparativeTablesView scenario={scenario} moduleId="budgets" />
      )}
      {show('overview') && (
        <ModuleSettingsPanel
          scenario={scenario}
          setScenario={setScenario}
          moduleId="budgets"
          title="Budgets · module settings"
          features={[
            { key: 'financialRatios',    label: 'Financial Ratios',       hint: '15 ratios, timeless lenses' },
            { key: 'ratiosTimeline',     label: 'Ratios Timeline view',   hint: 'Enables the Timeline toggle on Ratios' },
            { key: 'recurringTable',     label: 'Recurring subs table',   hint: 'All recurring lines with source-state labels' },
            { key: 'familyComposition',  label: 'Support AP composition', hint: 'Direct + routed channels' },
            { key: 'workingCapital',     label: 'Working Capital KPIs',   hint: 'DSO / DPO / CCC strip' },
            { key: 'arStreams',          label: 'AR streams',             hint: 'AR/AP by counterparty + contribution AR' },
            { key: 'formalStatements',   label: 'Formal statements',      hint: 'IS / BS / CF — period × density × direction' },
          ]}
        />
      )}
      {/* Proposal 8 Tier 2 — provenance chips. Budgets is the canonical
          "mix of Ledger + Schedule" zone: working-capital ratios are
          computed, recurring totals come from your schedules, and AR/AP
          totals blend both. */}
      {show('overview', 'working-capital') && features.workingCapital && (
      <div className={KPI_ROW}>
        <KPICard
          label={<>Monthly recurring (all tranches) <EduTip term="recurringSubs" /></>}
          value={<MoneyCell value={totalSubs} breakdown={[...recurringCategoryBreakdown(scenario, ym), ...jeExpenseBreakdown]} registryKey="recurringSubs" onJump={onJump} decimals={2} label="Monthly recurring (all tranches)" scenario={scenario} />}
          sub="M + Y/12 + Q·4/12"
          provenance="schedule"
          provenanceTip="Sum of monthly + (yearly ÷ 12) + (quarterly × 4 ÷ 12) from RECURRING schedules. Override any line via Settings → Piecewise."
        />
        <KPICard
          label={<>Support AP (direct + routed) <EduTip term="familyDirect" /></>}
          value={<MoneyCell value={famDirect + marian} breakdown={[
            ...famDirectBreakdown,
            { label: 'Routed support channel', amount: marian, source: 'routedChannel', cat: 'Support' },
          ]} registryKey="familyDirect" onJump={onJump} decimals={2} tone="warn" label="Support AP (direct + routed)" />}
          sub={`${fmtMoney(famDirect, { decimals: 0 })} + ${fmtMoney(marian, { decimals: 0 })}`}
          tone="warn"
          provenance="schedule"
          provenanceTip="Sum of direct support channels + routed support channel monthly amounts. Pin exact-date plans via Settings → Piecewise (AP cadence)."
        />
        <KPICard
          label={<>Contribution AR expected <EduTip term="educationAR" /></>}
          value={<MoneyCell value={eduExpected} registryKey="educationAR" term="educationAR" onJump={onJump} decimals={2} tone={eduExpected > 0 ? 'good' : undefined} label="Contribution AR (expected)" />}
          sub={`scheduled ${fmtMoney(eduScheduled, { decimals: 0 })}`}
          tone={eduExpected > 0 ? 'good' : undefined}
          provenance="computed"
          provenanceTip="Scheduled contributor receivables × per-contributor collection rate (historical). Adjust reusable assumptions in Settings -> Shared assumptions."
        />
        <KPICard
          label={<>Net contribution burden <EduTip term="netEducationBurden" /></>}
          value={<MoneyCell value={netEduBurden} decimals={2} tone={netEduBurden > 0 ? 'warn' : 'good'} term="netEducationBurden" registryKey="netEducationBurden" onJump={onJump} label="Net contribution burden" />}
          sub="Routed AP - contribution AR"
          tone={netEduBurden > 0 ? 'warn' : 'good'}
          provenance="computed"
          provenanceTip="Routed AP outflow minus Education AR inflow. Positive = net cash drain; negative = net inflow."
        />
        <KPICard
          label={<>DSO <EduTip term="DSO" /></>}
          value={`${calcDSO()} d`}
          sub="avg collection days"
          provenance="computed"
          provenanceTip="Days Sales Outstanding — weighted average of AR collection days across contributor, pooled, and tenant streams."
        />
        <KPICard
          label={<>DPO <EduTip term="DPO" /></>}
          value={`${calcDPO(scenario)} d`}
          sub="Net 15 + arrears drag"
          provenance="computed"
          provenanceTip="Days Payables Outstanding — net-15 default plus routed-channel arrears drag. Tunable via scenario assumptions."
        />
        <KPICard
          label={<>CCC <EduTip term="CCC" /></>}
          value={`${calcCashConversionCycle(scenario)} d`}
          sub="DSO − DPO (ideal: negative)"
          tone={calcCashConversionCycle(scenario) < 0 ? 'good' : 'warn'}
          provenance="computed"
          provenanceTip="Cash Conversion Cycle = DSO − DPO. Negative means you collect before you pay (the goal)."
        />
      </div>
      )}

      {show('overview', 'ratios') && features.financialRatios && (
        <SectionCard
          sectionId="ratios"
          title="Financial Ratios — timeless lenses"
          subtitle={ratiosView === 'snapshot'
            ? '15 ratios × 5 points in time (−3mo → now → +3mo) as inline sparklines. Toggle to Timeline for the full Period × Density × Direction data table.'
            : `15 ratios × ${budgetsPeriods} periods of ${budgetsDensity}-month each (${budgetsDirLabel}). Same ratio definitions computed at every column. Direction + horizon come from the model context bar.`}
          right={features.ratiosTimeline ? (
            <div className="inline-flex border border-border rounded-sm overflow-hidden">
              <button
                type="button"
                className={`bg-transparent border-0 px-[10px] py-[3px] text-[0.72rem] font-[inherit] cursor-pointer border-r border-border ${ratiosView === 'snapshot' ? 'bg-surface-el text-accent-gold font-semibold' : 'text-text-muted hover:text-text'}`}
                onClick={() => setRatiosView('snapshot')}
                aria-pressed={ratiosView === 'snapshot'}
              >
                Snapshot
              </button>
              <button
                type="button"
                className={`bg-transparent border-0 px-[10px] py-[3px] text-[0.72rem] font-[inherit] cursor-pointer ${ratiosView === 'timeline' ? 'bg-surface-el text-accent-gold font-semibold' : 'text-text-muted hover:text-text'}`}
                onClick={() => setRatiosView('timeline')}
                aria-pressed={ratiosView === 'timeline'}
              >
                Timeline
              </button>
            </div>
          ) : null}
        >
          {/* Ratios Timeline view — extra feature gate. If timeline is off,
              snapshot-only mode is forced. */}
          {ratiosView === 'snapshot' || !features.ratiosTimeline
            ? <FinancialRatiosGrid
                ratios={financialRatios}
                gradient={!!scenario?.sparklineGradient}
                semanticTone={!!scenario?.sparklineSemanticTone}
                showSparkline={verbosityAtLeast(scenario, 'standard')}
              />
            : <MetricHorizonTable
                title="Financial Ratios across horizon"
                entity="Current Lens Entity"
                subtitle={`${ratiosTimeline.periods} periods × ${ratiosTimeline.density}-month · ${ratiosTimeline.direction}`}
                periods={ratiosTimeline.periodLabels}
                currentPeriodLabel={ratiosTimeline.currentLabel}
                currentPeriodProof={ratiosTimeline.currentState}
                rows={ratiosTimeline.rows}
                showSparkline={verbosityAtLeast(scenario, 'standard') && scenario?.showTrendColumn !== false}
                showRowSparkline={verbosityAtLeast(scenario, 'standard') && scenario?.showRowSparkline !== false}
                gradient={!!scenario?.sparklineGradient}
                semanticTone={!!scenario?.sparklineSemanticTone}
                guides={scenario?.guides}
                sparkIdPrefix="budgets-ratios-timeline"
              />}
        </SectionCard>
      )}

      {show('overview') && adj.count > 0 && (
        <SectionCard
          title={`Adjusting Entries — ${adj.count} posted`}
          subtitle={`Net IS impact: ${isExpenseAdj >= 0 ? '+' : ''}${fmtMoney(isExpenseAdj, { decimals: 0 })} ${isExpenseAdj >= 0 ? 'added expense' : 'expense reversed'}. Toggle Include JEs in the model context bar.`}
        >
          <ul className={`${COMPACT_LIST} list-none pl-0`}>
            {adj.entries.filter(e => e.status === 'posted').map(e => {
              const net = e.lines.reduce((s, l) => s + ((Number(l.debit) || 0) - (Number(l.credit) || 0)), 0);
              return (
                <li key={e.id} className="px-[0.5rem] py-[0.4rem] bg-surface-el border border-border rounded-sm mb-[0.3rem] text-[0.85rem]">
                  <strong className="text-accent-gold font-mono">{e.date}</strong> — {e.memo || '(no memo)'}
                  {' · '}<em className="not-italic text-text-muted text-[0.75rem]">{(e.affects ?? []).join('+').toUpperCase() || '—'}</em>
                  {' · '}<span className={net > 0 ? 'text-accent-red tabular-nums' : 'text-accent-green tabular-nums'}>{fmtMoney(net, { decimals: 0 })}</span>
                  <br/>
                  <small className="text-text-dim">
                    {e.lines.map((l, i) => (
                      <span key={i}>
                        {Number(l.debit) > 0 ? `DR ${l.account} ${fmtMoney(l.debit, { decimals: 0 })}` : `CR ${l.account} ${fmtMoney(l.credit, { decimals: 0 })}`}
                        {i < e.lines.length - 1 ? ' / ' : ''}
                      </span>
                    ))}
                  </small>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      )}

      {show('overview', 'ar-streams') && features.arStreams && (
      <SectionCard
        sectionId="ar-streams"
        title="AR / AP by counterparty"
        subtitle="Where the money flows — outflow (red) and inflow (green) per channel, ranked by total magnitude."
      >
        <ARAPStackedChart
          data={arApRows}
          stackKeys={[
            { key: 'ap', name: 'Outflow (AP)', fill: '#f87171' },
            { key: 'ar', name: 'Inflow (AR)',  fill: '#4ade80' },
          ]}
        />
      </SectionCard>
      )}

      {show('overview', 'recurring', 'payables') && (features.recurringTable || features.familyComposition) && (
      <div className={GRID_2}>
        {show('overview', 'recurring') && features.recurringTable && (
        <SectionCard sectionId="recurring" title="Recurring spend — by category" subtitle="Monthly amortized (includes yearly/12 + quarterly·4/12)">
          <RecurringByCategoryChart data={recurringByCategory()} />
          <div className={KPI_ROW_COMPACT}>
            <KPICard label="Monthly subs" value={<MoneyCell value={monthlySubs} decimals={2} registryKey="recurringSubs" onJump={onJump} label="Monthly subscriptions" />} provenance="schedule" provenanceTip="Sum of monthly recurring subscriptions from RECURRING.monthly." />
            <KPICard label="Yearly ÷ 12" value={<MoneyCell value={yearlyAmort} decimals={2} registryKey="recurringSubs" onJump={onJump} label="Yearly subscriptions ÷ 12" />} provenance="computed" provenanceTip="Sum of yearly subscriptions amortized to monthly equivalent (RECURRING.yearly ÷ 12)." />
            <KPICard label="Quarterly amort." value={<MoneyCell value={quarterlyAmort} decimals={2} registryKey="recurringSubs" onJump={onJump} label="Quarterly subscriptions amortized" />} provenance="computed" provenanceTip="Sum of quarterly subscriptions amortized to monthly equivalent (RECURRING.quarterly × 4 ÷ 12)." />
          </div>
        </SectionCard>
        )}

        {show('overview', 'payables') && features.familyComposition && (
        <NestedSection
          sectionId="budgets-family-composition"
          title="Support AP composition"
          subtitle={`How support AP splits across channels${familyDirectModified ? ' · ● direct support schedule modified' : ''}${marianModified ? ' · ● routed schedule modified' : ''}`}
          scenario={scenario} setScenario={setScenario}
          headline={<FamilyCompositionChart data={famComposition} />}
          topDrivers={
            <div>
              {famTop3.map((c, i) => (
                <div key={i} className={DRIVER_ROW}>
                  <div>
                    <span className="text-text">{c.label}</span>
                    <span className="text-text-dim text-[0.75rem]"> · {c.cat}</span>
                  </div>
                  <span className="text-text tabular-nums font-semibold">{fmtMoney(c.amount, { decimals: 0 })}</span>
                </div>
              ))}
            </div>
          }
          fullCount={famAllRows.length}
          fullList={
            <>
              <ul className={COMPACT_LIST}>
                {FAMILY_AP.direct.channels.map((c, i) => (
                  <li key={i}>
                    <strong className="text-text">{fmtMoney(c.estMonthly)}</strong> — {c.name}{' '}
                    <em className="not-italic text-text-muted">({c.cat})</em>
                    {familyDirectModified && i === 0 && <ModifiedBadge active reason={OVERRIDE_REASON.familyDirect} />}
                  </li>
                ))}
                <li>
                  <strong className="text-text">{fmtMoney(marian)}</strong> — routed support channel
                  {marianModified && <ModifiedBadge active reason={OVERRIDE_REASON.marian} />}
                </li>
              </ul>
              <p className={HINT_WARN}>⚠ Open question: {FAMILY_AP.viaMarian.openQuestion}</p>
            </>
          }
        />
        )}
      </div>
      )}

      {show('overview', 'ar-streams') && features.arStreams && (
      <NestedSection
        sectionId="budgets-sibling-ar"
        title="Contribution AR — collection rates"
        subtitle="Toggle contribution modes and edit support amounts in Settings; collectionRate is baked into expected cash"
        scenario={scenario} setScenario={setScenario}
        headline={
          <div className={KPI_ROW_COMPACT}>
            <KPICard label="Total scheduled" value={<MoneyCell value={siblingFooter.scheduled} decimals={2} term="educationAR" registryKey="educationAR" onJump={onJump} label="Total scheduled contribution AR" />} provenance="schedule" provenanceTip="Sum of scheduled contribution AR amounts before applying historical collection rates." />
            <KPICard label="Total expected (rate-adj)" value={<MoneyCell value={siblingFooter.expected} decimals={2} tone="good" term="educationAR" registryKey="educationAR" onJump={onJump} label="Total expected (rate-adjusted)" />} provenance="computed" provenanceTip="Scheduled AR × per-contributor collection rate (historical observation). Rate-adjusted expected inflow." />
          </div>
        }
        topDrivers={
          <div>
            {top3Siblings.map((r) => (
              <div key={r.id} className={DRIVER_ROW}>
                <div>
                  <span className="text-text">{r.sibling}</span>
                  <span className="text-text-dim text-[0.75rem]"> · {fmtPct(r.rate)} rate</span>
                </div>
                <span className="text-text tabular-nums font-semibold">{fmtMoney(r.expected, { decimals: 0 })}</span>
              </div>
            ))}
          </div>
        }
        fullCount={siblingRows.length}
        fullList={
          <DataTable
            columns={[
              { key: 'sibling',   label: 'Contributor', width: '28%' },
              { key: 'scheduled', label: 'Scheduled',  align: 'right', width: '15%', format: moneyFmt },
              { key: 'rate',      label: 'Historic rate', align: 'right', width: '15%', format: (v) => v == null ? '—' : fmtPct(v) },
              { key: 'expected',  label: 'Expected cash', align: 'right', width: '17%', format: moneyFmt },
              { key: 'note',      label: 'Pattern note' },
            ]}
            rows={siblingRows}
            footer={siblingFooter}
          />
        }
      />
      )}

      {show('overview', 'payables') && features.familyComposition && (
      <NestedSection
        sectionId="budgets-marian-cadence"
        title="Routed support — actual payment cadence"
        subtitle={`Arrears ${fmtMoney(marianArrears())} as of ${FAMILY_AP.viaMarian.arrearsAsOf}; Net 15 terms, lumpy 6-8 week pattern`}
        scenario={scenario} setScenario={setScenario}
        headline={
          <div className={KPI_ROW_COMPACT}>
            <KPICard label="Payments observed" value={`${marianPayments.length}`} sub="past 10 months" provenance="ledger" provenanceTip="Count of actual routed support payments observed in the past 10 months — from posted bank transactions." />
            <KPICard label="Total observed" value={<MoneyCell value={marianPayments.reduce((s, p) => s + p.amount, 0)} decimals={2} registryKey="marianAP" onJump={onJump} label="Total routed payments observed" />} provenance="ledger" provenanceTip="Sum of every routed support payment observed in BALANCES bank transactions over the past 10 months." />
            <KPICard label="Arrears" value={<MoneyCell value={marianArrears()} decimals={2} tone="warn" term="marianArrears" registryKey="marianArrears" onJump={onJump} label="Routed channel arrears" />} provenance="computed" provenanceTip="Net 15 expectation times months elapsed minus total observed payments. The drag from late-payment cadence." />
          </div>
        }
        topDrivers={
          <div>
            {top3Marian.map((p) => (
              <div key={p.id} className={DRIVER_ROW}>
                <div>
                  <span className="text-text">{p.date}</span>
                  <span className="text-text-dim text-[0.75rem]"> · {p.channel}</span>
                </div>
                <span className="text-text tabular-nums font-semibold">{fmtMoney(p.amount, { decimals: 0 })}</span>
              </div>
            ))}
          </div>
        }
        fullCount={marianPayments.length}
        fullList={
          <DataTable
            columns={[
              { key: 'date',    label: 'Date', width: '22%' },
              { key: 'amount',  label: 'Amount', align: 'right', width: '22%', format: moneyFmt },
              { key: 'channel', label: 'Channel' },
            ]}
            rows={marianPayments}
            footer={{
              date: `${marianPayments.length} payments`,
              amount: marianPayments.reduce((s, p) => s + p.amount, 0),
              channel: 'Total observed (past 10 months)',
            }}
          />
        }
      />
      )}

      {show('overview', 'recurring') && features.recurringTable && (
      <NestedSection
        sectionId="budgets-all-recurring"
        title="All recurring lines — source-state labeled"
        subtitle={`Monthly equivalent column drives the burn model. Google One AI Pro downgrades next month.${hiddenRecurring ? ` · ${hiddenRecurring} folded (toggle in Settings → Account Visibility)` : ''}${overriddenRecurringCount ? ` · ● ${overriddenRecurringCount} modified from default` : ''}`}
        scenario={scenario} setScenario={setScenario}
        headline={
          <div className={KPI_ROW_COMPACT}>
            <KPICard label="Total monthly recurring" value={<MoneyCell value={totalSubs} decimals={2} breakdown={recurringCategoryBreakdown(scenario, ym)} registryKey="recurringSubs" onJump={onJump} label="Total monthly recurring" scenario={scenario} />} sub={`${recurringRows.length} lines`} provenance="schedule" provenanceTip="Sum of every visible recurring line (monthly + yearly amortized + quarterly amortized) — your recurring schedule's monthly equivalent." />
          </div>
        }
        topDrivers={
          <div>
            {top3Recurring.map((r) => (
              <div key={r.id} className={DRIVER_ROW}>
                <div>
                  <span className="text-text">{r.code} · {r.vendor}</span>
                  <span className="text-text-dim text-[0.75rem]"> · {r.cat} · {r.freq}</span>
                </div>
                <span className="text-text tabular-nums font-semibold">{fmtMoney(r.monthly, { decimals: 0 })}</span>
              </div>
            ))}
          </div>
        }
        fullCount={recurringRows.length}
        fullList={
          <DataTable
            compact
            columns={[
              { key: 'code',    label: 'Code',    width: '10%' },
              { key: 'vendor',  label: 'Vendor',  width: '34%',
                format: (v, row) => (
                  <span title={row.__note || (row.zohoRef ? `source ref: ${row.zohoRef}` : undefined)}>
                    {v}
                    {row._overridden && <ModifiedBadge active reason={OVERRIDE_REASON.recurring} />}
                    {row.__note && <em className={HINT_INLINE}> ⏱</em>}
                    {row.forecastOnly && <span className={BADGE_FORECAST} title="Engine includes in totals; surfaced via imported bill snapshots">FORECAST</span>}
                  </span>
                ),
              },
              { key: 'cat',     label: 'Cat.',    width: '12%' },
              { key: 'freq',    label: 'Frequency', width: '13%' },
              { key: 'amount',  label: 'Billed',  align: 'right', width: '14%',
                format: (v, row) => <MoneyCell value={v} decimals={2} fxHint={row.fxHint} label={`${row.vendor} — billed`} scenario={scenario} />,
              },
              { key: 'monthly', label: 'Monthly eq.', align: 'right', width: '14%',
                format: (v, row) => <MoneyCell value={v} decimals={2} fxHint={row.fxHint} label={`${row.vendor} — monthly equivalent`} scenario={scenario} />,
              },
            ]}
            rows={recurringRows}
            footer={{
              code: '', vendor: 'Total monthly recurring', cat: '', freq: '', amount: '', monthly: totalSubs,
            }}
          />
        }
      />
      )}

      {show('overview', 'formal') && features.formalStatements && (<>
      <FormalStatementSection
        tabId="budgets" stmtKey="is"
        label="Income Statement (formal)"
        subtitle={budgetsPeriods > 1
          ? `${budgetsPeriods}-period ${budgetsDirLabel} (managerial · ratio trend, synthetic local past until provider proof)`
          : `Accrual basis · Net income: ${fmtMoney(is.netIncome, { decimals: 0 })}`}
        title="Statement of Income — Accrual Basis"
        entity="Current Lens Entity"
        period={budgetsPeriods > 1 ? `${budgetsPeriodLabels[0]} – ${budgetsPeriodLabels[budgetsPeriodLabels.length - 1]}` : `For the month ending ${ym}-30`}
        periods={budgetsPeriods > 1 ? budgetsPeriodLabels : undefined}
        sections={budgetsPeriods > 1 ? shapeIncomeStatementMulti(budgetsProjection.incomeStatements) : shapeIncomeStatement(is)}
        currentPeriodLabel={budgetsCurrentLabel}
        currentPeriodProof={budgetsCurrentProof}
        scenario={scenario} setScenario={setScenario}
      />
      <FormalStatementSection
        tabId="budgets" stmtKey="bs"
        label="Balance Sheet (formal)"
        subtitle={budgetsPeriods > 1
          ? `${budgetsPeriods}-period ${budgetsDirLabel} BS · A = L + E (managerial · synthetic local past until provider proof)`
          : `A = L + E · Assets ${fmtMoney(assetsLiquid + fixedAssetsNet, { decimals: 0 })} · Liab ${fmtMoney(ccDebtV72 - ccCreditsV72 + ltlV72, { decimals: 0 })}`}
        title="Statement of Financial Position"
        entity="Current Lens Entity"
        period={budgetsPeriods > 1 ? `${budgetsPeriodLabels[0]} – ${budgetsPeriodLabels[budgetsPeriodLabels.length - 1]}` : `As of ${ym}-30`}
        periods={budgetsPeriods > 1 ? budgetsPeriodLabels : undefined}
        sections={budgetsPeriods > 1
          ? shapeBalanceSheetMulti(budgetsProjection.balanceSheets)
          : shapeBalanceSheet({
              assetsLiquid, fixedAssetsNet,
              ccDebt: ccDebtV72, ccCredits: ccCreditsV72, ltl: ltlV72,
              eq, susTotal,
            })}
        currentPeriodLabel={budgetsCurrentLabel}
        currentPeriodProof={budgetsCurrentProof}
        scenario={scenario} setScenario={setScenario}
      />
      <FormalStatementSection
        tabId="budgets" stmtKey="cf"
        label="Cash Flow Statement (formal)"
        subtitle={budgetsPeriods > 1
          ? `${budgetsPeriods}-period ${budgetsDirLabel} CF · CFO + CFI + CFF (managerial · synthetic local past until provider proof)`
          : 'CFO + CFI + CFF = Net change in cash · single-month snapshot'}
        title="Statement of Cash Flows"
        entity="Current Lens Entity"
        period={budgetsPeriods > 1 ? `${budgetsPeriodLabels[0]} – ${budgetsPeriodLabels[budgetsPeriodLabels.length - 1]}` : `For the month ending ${ym}-30`}
        periods={budgetsPeriods > 1 ? budgetsPeriodLabels : undefined}
        sections={budgetsPeriods > 1
          ? shapeCashFlowStatementMulti(budgetsProjection.cashFlows)
          : shapeCashFlowStatement(buildCashFlowSnapshot(scenario, ym))}
        currentPeriodLabel={budgetsCurrentLabel}
        currentPeriodProof={budgetsCurrentProof}
        scenario={scenario} setScenario={setScenario}
      />
      </>)}

      {/* Proposal 1 (2026-04-25) — module data inheritance. Budgets sees
          Actuals as upstream context: variance baselining + the past
          ratios that anchor the working-capital story. Faded panel at
          the bottom; collapse persists per-tab in local browser state. */}
      {show('overview') && (
        <ContextPanel currentModule="budgets" contextModule="actuals" scenario={scenario} />
      )}
    </div>
  );
}

/* v8 Path I — FinancialRatiosGrid
   Groups ratios by category (Liquidity / Solvency / Profitability /
   Efficiency) into column cards. Each ratio shows:
   - value (tone-colored, formatted by type)
   - 5-point sparkline (past-3mo → past-1mo → now → fwd-1mo → fwd-3mo)
   - delta badge showing fwd-vs-past trend direction
   - rich tooltip: formula · benchmark hint · full explanation
*/
const RATIO_CARD = 'flex flex-col gap-[0.55rem] bg-surface-el border border-border rounded-md p-[0.75rem] min-w-0';
const RATIO_COL  = 'grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-3';
const RATIO_ROW  = 'grid grid-cols-[1fr_auto_auto] gap-2 items-center px-[0.35rem] py-[0.45rem] border-b border-dashed border-border last:border-b-0';
const TONE_CLS = {
  good:    'text-accent-green',
  warn:    'text-accent-gold',
  bad:     'text-accent-red',
  neutral: 'text-text',
};
function fmtRatioValue(r) {
  if (r.value == null || !Number.isFinite(r.value)) return '—';
  switch (r.format) {
    case 'pct':    return `${(r.value * 100).toFixed(1)}%`;
    case 'days':   return `${Math.round(r.value)}d`;
    case 'months': return `${r.value.toFixed(1)}mo`;
    case 'x':      return `${r.value.toFixed(2)}×`;
    case 'ratio':
    default:       return r.value.toFixed(2);
  }
}

function FinancialRatiosGrid({ ratios, gradient, showSparkline = true, semanticTone = false }) {
  const categories = ['Liquidity', 'Solvency', 'Profitability', 'Efficiency'];
  const byCategory = categories.reduce((m, c) => {
    m[c] = ratios.filter(r => r.category === c);
    return m;
  }, {});
  return (
    <div className={RATIO_COL}>
      {categories.map((cat) => (
        <div key={cat} className={RATIO_CARD}>
          <div className="text-[0.7rem] font-bold uppercase tracking-[0.08em] text-accent-gold">{cat}</div>
          <div>
            {byCategory[cat].map((r) => (
              <div
                key={r.ratio}
                className={RATIO_ROW}
              >
                <Tooltip
                  placement="right"
                  delay={100}
                  maxWidth={360}
                  content={<RichExplanation title={r.ratio} formula={r.formula} hint={r.hint} explanation={r.explanation} />}
                >
                  <span className="flex flex-col min-w-0 cursor-help">
                    <span className="text-[0.82rem] text-text truncate">{r.ratio}</span>
                    {r.formula && <em className="text-[0.66rem] text-text-dim not-italic truncate">{r.formula}</em>}
                  </span>
                </Tooltip>
                <span className="flex items-center gap-[6px] whitespace-nowrap">
                  {showSparkline && (() => {
                    /* Sparkline tone is a TREND tone (is the series
                       rising/falling under the user's semantic toggle),
                       distinct from r.tone which is a VALUE tone
                       (is the current value good/warn/bad vs thresholds).
                       Using r.tone on the sparkline ignores semanticTone;
                       resolveTrendVisual honors it. */
                    const { tone: trendTone, series: plotSeries } = resolveTrendVisual(
                      r.series, r.format, r.betterWhen, semanticTone,
                    );
                    return (
                      <>
                        <MiniSparkline
                          series={plotSeries}
                          tone={trendTone}
                          size="sm"
                          gradient={gradient}
                          highlightIdx={2}
                          ariaLabel={`${r.ratio} trend`}
                        />
                        <SparklineDeltaBadge series={plotSeries} format={r.format} />
                      </>
                    );
                  })()}
                  {!showSparkline && <SparklineDeltaBadge series={r.series} format={r.format} />}
                </span>
                <strong className={`text-[0.88rem] tabular-nums font-semibold whitespace-nowrap ${TONE_CLS[r.tone] || TONE_CLS.neutral}`}>
                  {fmtRatioValue(r)}
                </strong>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="col-span-full text-[0.7rem] text-text-dim italic mt-1 m-0 px-[0.35rem]">
        Sparkline shows ratio trend across −3mo → −1mo → now → +1mo → +3mo. Hover any ratio for formula + full explanation. Same discipline lenses, viewed through time.
      </p>
    </div>
  );
}
